"use client";

import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

interface TermsOfServiceContentProps {
  lastUpdated: string;
  structuredData: Record<string, unknown>;
}

export default function TermsOfServiceContent({
  lastUpdated,
  structuredData,
}: TermsOfServiceContentProps) {
  return (
    <>
      {/* Structured Data */}
      <script
        // biome-ignore lint/security/noDangerouslySetInnerHtml: Required for SEO structured data
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        type="application/ld+json"
      />

      <div className="min-h-screen bg-background">
        {/* Header Section */}
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-b from-muted/30 to-background py-16 md:py-24"
          initial={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-4xl text-center">
              <Badge className="mb-4" variant="secondary">
                Legal Information
              </Badge>
              <h1 className="heading-display mb-6 font-bold font-heading text-4xl text-foreground md:text-5xl lg:text-6xl">
                Terms of Service
              </h1>
              <p className="body-text-base mx-auto mb-8 max-w-2xl text-muted-foreground text-optimal">
                Please read these terms carefully before using our website or
                purchasing our products. These terms govern your relationship
                with WillPower Store.
              </p>
              <div className="flex items-center justify-center gap-2 text-muted-foreground text-sm">
                <span>Last updated:</span>
                <time dateTime="2025-01-17">{lastUpdated}</time>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Content Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="mx-auto max-w-4xl">
            {/* Business Information */}
            <motion.div
              className="mb-12"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-2xl">
                    Business Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <h4 className="mb-2 font-bold font-heading text-foreground">
                        Trading Details
                      </h4>
                      <div className="space-y-1 text-muted-foreground text-sm">
                        <p>WillPower Store</p>
                        <p>13 Great Western Arcade</p>
                        <p>Birmingham, B32QD</p>
                        <p>United Kingdom</p>
                      </div>
                    </div>
                    <div>
                      <h4 className="mb-2 font-bold font-heading text-foreground">
                        Contact Information
                      </h4>
                      <div className="space-y-1 text-muted-foreground text-sm">
                        <p>Email: willpowerwellbeing@hotmail.com</p>
                        <p>Instagram: @willpowergymstoreuk</p>
                        <p>Website: willpower-store.com</p>
                        <p>Established: 2014</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Terms Sections */}
            <div className="space-y-12">
              {/* Section 1: Agreement to Terms */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <h2 className="mb-6 font-bold font-heading text-2xl text-foreground md:text-3xl">
                  1. Agreement to Terms
                </h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-optimal leading-relaxed">
                    These Terms and Conditions constitute a legally binding
                    agreement between you and WillPower Store ("we," "us," or
                    "our") regarding your use of our website willpower-store.com
                    and purchase of our supplement products.
                  </p>
                  <div className="rounded-lg bg-muted/30 p-4">
                    <p className="text-muted-foreground text-sm">
                      <strong>
                        By using our website or making a purchase, you agree to
                        these terms.
                      </strong>{" "}
                      If you do not agree with these terms, please do not use
                      our website or services.
                    </p>
                  </div>
                </div>
              </motion.section>

              <Separator />

              {/* Section 2: Products and Services */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <h2 className="mb-6 font-bold font-heading text-2xl text-foreground md:text-3xl">
                  2. Products and Services
                </h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-optimal leading-relaxed">
                    WillPower Store operates as an authorised reseller of
                    supplements from established third-party manufacturers and
                    brands:
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Card>
                      <CardContent className="pt-6">
                        <h4 className="mb-3 font-bold font-heading text-foreground">
                          Product Range
                        </h4>
                        <ul className="space-y-1 text-muted-foreground text-sm">
                          <li>• Sports nutrition supplements</li>
                          <li>• Protein powders and bars</li>
                          <li>• Vitamins and minerals</li>
                          <li>• Pre and post-workout supplements</li>
                          <li>• Health and wellness products</li>
                        </ul>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <h4 className="mb-3 font-bold font-heading text-foreground">
                          Service Options
                        </h4>
                        <ul className="space-y-1 text-muted-foreground text-sm">
                          <li>• In-store pickup (Birmingham city centre)</li>
                          <li>• Online ordering and payment</li>
                          <li>• Expert advice and recommendations</li>
                          <li>• Delivery services (coming soon)</li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <p className="text-blue-800 text-sm">
                      <strong>Reseller Notice:</strong> We are an authorised
                      reseller of third-party branded products. All products are
                      manufactured by their respective brand owners, not by
                      WillPower Store. Product warranties and guarantees are
                      provided by the original manufacturers.
                    </p>
                  </div>
                  <div className="rounded-lg bg-muted/30 p-4">
                    <p className="text-muted-foreground text-sm">
                      All product descriptions, prices, and availability are
                      subject to change without notice. We reserve the right to
                      discontinue products at any time based on manufacturer
                      availability.
                    </p>
                  </div>
                </div>
              </motion.section>

              <Separator />

              {/* Section 3: Ordering and Payment */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <h2 className="mb-6 font-bold font-heading text-2xl text-foreground md:text-3xl">
                  3. Ordering and Payment
                </h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-optimal leading-relaxed">
                    Our ordering process is designed to be simple and secure:
                  </p>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="grid gap-6 md:grid-cols-2">
                        <div>
                          <h4 className="mb-3 font-bold font-heading text-foreground">
                            Order Process
                          </h4>
                          <ul className="space-y-1 text-muted-foreground text-sm">
                            <li>• Orders are subject to acceptance by us</li>
                            <li>• Payment required at time of ordering</li>
                            <li>• Order confirmation sent via email</li>
                            <li>• Pickup notification when ready</li>
                            <li>• All sales are final</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="mb-3 font-bold font-heading text-foreground">
                            Payment Terms
                          </h4>
                          <ul className="space-y-1 text-muted-foreground text-sm">
                            <li>• All prices in British Pounds Sterling (£)</li>
                            <li>• VAT included where applicable</li>
                            <li>• Secure payment processing via Shopify</li>
                            <li>• Major credit and debit cards accepted</li>
                            <li>• No refunds except for faulty products</li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <p className="text-amber-800 text-sm">
                      <strong>Please Note:</strong> All sales are final. We do
                      not offer refunds for change of mind. Please ensure you
                      are certain about your purchase before completing payment.
                    </p>
                  </div>
                </div>
              </motion.section>

              <Separator />

              {/* Section 4: Consumer Rights (UK) */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <h2 className="mb-6 font-bold font-heading text-2xl text-foreground md:text-3xl">
                  4. Consumer Rights (UK)
                </h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-optimal leading-relaxed">
                    Under the Consumer Rights Act 2015, you have important
                    rights regarding product quality. As a reseller, we are
                    responsible for ensuring these rights are met:
                  </p>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="grid gap-6 md:grid-cols-2">
                        <div>
                          <h4 className="mb-3 font-bold font-heading text-foreground">
                            Product Quality Rights
                          </h4>
                          <ul className="space-y-1 text-muted-foreground text-sm">
                            <li>• Products must be of satisfactory quality</li>
                            <li>• Must be fit for purpose</li>
                            <li>• Must match product descriptions</li>
                            <li>• Free from defects at time of sale</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="mb-3 font-bold font-heading text-foreground">
                            Reseller Responsibilities
                          </h4>
                          <ul className="space-y-1 text-muted-foreground text-sm">
                            <li>• Replacement for faulty products</li>
                            <li>• Liaison with manufacturers for warranties</li>
                            <li>• Refund only for faulty items</li>
                            <li>• Must report faults promptly</li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <div className="rounded-lg bg-muted/30 p-4">
                    <p className="text-muted-foreground text-sm">
                      <strong>Important:</strong> We do not offer refunds for
                      change of mind. Refunds are only available for faulty
                      products that do not meet quality standards. For warranty
                      claims beyond our control, we will facilitate contact with
                      the original manufacturer.
                    </p>
                  </div>
                </div>
              </motion.section>

              <Separator />

              {/* Section 5: No Refunds Policy */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <h2 className="mb-6 font-bold font-heading text-2xl text-foreground md:text-3xl">
                  5. No Refunds Policy
                </h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-optimal leading-relaxed">
                    Please read this section carefully before making a purchase:
                  </p>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="grid gap-6 md:grid-cols-2">
                        <div>
                          <h4 className="mb-3 font-bold font-heading text-foreground">
                            General Policy
                          </h4>
                          <ul className="space-y-1 text-muted-foreground text-sm">
                            <li>• All sales are final</li>
                            <li>• No refunds for change of mind</li>
                            <li>• No returns for unopened products</li>
                            <li>• Please choose carefully before purchasing</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="mb-3 font-bold font-heading text-foreground">
                            Faulty Products Only
                          </h4>
                          <ul className="space-y-1 text-muted-foreground text-sm">
                            <li>• Refunds only for genuinely faulty items</li>
                            <li>• Must be reported within 7 days of pickup</li>
                            <li>• Proof of fault required</li>
                            <li>• Replacement preferred over refund</li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                    <p className="text-red-800 text-sm">
                      <strong>Important Notice:</strong> We do not accept
                      returns or provide refunds for any reason other than
                      genuinely faulty products. Please ensure you are certain
                      about your purchase before completing your order.
                    </p>
                  </div>
                  <div className="rounded-lg bg-muted/30 p-4">
                    <p className="text-muted-foreground text-sm">
                      <strong>Why this policy?</strong> As a supplement
                      reseller, we maintain strict hygiene and safety standards.
                      Once products leave our store, we cannot guarantee their
                      storage conditions or integrity. Additionally, as a
                      reseller, we must balance manufacturer policies with
                      customer service.
                    </p>
                  </div>
                </div>
              </motion.section>

              <Separator />

              {/* Section 6: Manufacturer Warranties and Third-Party Products */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <h2 className="mb-6 font-bold font-heading text-2xl text-foreground md:text-3xl">
                  6. Manufacturer Warranties and Third-Party Products
                </h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-optimal leading-relaxed">
                    As a reseller, we facilitate access to manufacturer
                    warranties and support:
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Card>
                      <CardContent className="pt-6">
                        <h4 className="mb-3 font-bold font-heading text-foreground">
                          Manufacturer Warranties
                        </h4>
                        <ul className="space-y-1 text-muted-foreground text-sm">
                          <li>
                            • Warranties provided by original manufacturers
                          </li>
                          <li>
                            • We facilitate warranty claims where possible
                          </li>
                          <li>• Warranty terms vary by brand and product</li>
                          <li>• Original packaging and receipts required</li>
                        </ul>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <h4 className="mb-3 font-bold font-heading text-foreground">
                          Third-Party Relationships
                        </h4>
                        <ul className="space-y-1 text-muted-foreground text-sm">
                          <li>• We are not affiliated with manufacturers</li>
                          <li>• Product information sourced from suppliers</li>
                          <li>• Manufacturer policies may override ours</li>
                          <li>• Direct manufacturer contact may be required</li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <p className="text-amber-800 text-sm">
                      <strong>Important:</strong> While we strive to assist with
                      warranty claims, the ultimate responsibility for product
                      warranties lies with the original manufacturer. We cannot
                      guarantee manufacturer response times or outcomes.
                    </p>
                  </div>
                </div>
              </motion.section>

              <Separator />

              {/* Section 7: Pickup and Delivery */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <h2 className="mb-6 font-bold font-heading text-2xl text-foreground md:text-3xl">
                  7. Pickup and Delivery
                </h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-optimal leading-relaxed">
                    We offer convenient collection and delivery options:
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Card>
                      <CardContent className="pt-6">
                        <h4 className="mb-3 font-bold font-heading text-foreground">
                          Store Pickup (Current)
                        </h4>
                        <ul className="space-y-1 text-muted-foreground text-sm">
                          <li>• Birmingham city centre location</li>
                          <li>• Order ready notification via email</li>
                          <li>• Convenient for office workers</li>
                          <li>• Expert advice available in-store</li>
                        </ul>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <h4 className="mb-3 font-bold font-heading text-foreground">
                          Delivery Service (Coming Soon)
                        </h4>
                        <ul className="space-y-1 text-muted-foreground text-sm">
                          <li>• UK-wide delivery options</li>
                          <li>• Tracked and insured shipping</li>
                          <li>• Delivery timeframes to be confirmed</li>
                          <li>• Competitive delivery rates</li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="rounded-lg bg-muted/30 p-4">
                    <p className="text-muted-foreground text-sm">
                      Risk of loss or damage passes to you upon pickup or
                      delivery. We are not liable for delays caused by
                      third-party couriers.
                    </p>
                  </div>
                </div>
              </motion.section>

              <Separator />

              {/* Section 8: User Responsibilities */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <h2 className="mb-6 font-bold font-heading text-2xl text-foreground md:text-3xl">
                  8. User Responsibilities
                </h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-optimal leading-relaxed">
                    When using our website and services, you agree to:
                  </p>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <Card>
                      <CardContent className="pt-6">
                        <h4 className="mb-3 font-bold font-heading text-foreground">
                          Account Requirements
                        </h4>
                        <ul className="space-y-1 text-muted-foreground text-sm">
                          <li>• Provide accurate personal information</li>
                          <li>• Maintain account security</li>
                          <li>• One account per person</li>
                          <li>• Notify us of any security breaches</li>
                        </ul>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <h4 className="mb-3 font-bold font-heading text-foreground">
                          Prohibited Activities
                        </h4>
                        <ul className="space-y-1 text-muted-foreground text-sm">
                          <li>• Using our website for unlawful purposes</li>
                          <li>• Violating applicable laws or regulations</li>
                          <li>• Infringing intellectual property rights</li>
                          <li>• Transmitting harmful or malicious code</li>
                        </ul>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <h4 className="mb-3 font-bold font-heading text-foreground">
                          Communication Guidelines
                        </h4>
                        <ul className="space-y-1 text-muted-foreground text-sm">
                          <li>
                            • Use respectful language in all communications
                          </li>
                          <li>• Instagram DMs for quick questions only</li>
                          <li>• Email for detailed enquiries and support</li>
                          <li>• Business hours: Mon-Sat 9am-6pm</li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="rounded-lg bg-muted/30 p-4">
                    <p className="text-muted-foreground text-sm">
                      We may suspend or terminate accounts that violate these
                      terms. You are responsible for all activities under your
                      account.
                    </p>
                  </div>
                </div>
              </motion.section>

              <Separator />

              {/* Section 9: Intellectual Property */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <h2 className="mb-6 font-bold font-heading text-2xl text-foreground md:text-3xl">
                  9. Intellectual Property
                </h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-optimal leading-relaxed">
                    All content on our website is protected by intellectual
                    property laws:
                  </p>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="grid gap-6 md:grid-cols-2">
                        <div>
                          <h4 className="mb-3 font-bold font-heading text-foreground">
                            Our Content
                          </h4>
                          <ul className="space-y-1 text-muted-foreground text-sm">
                            <li>• Website design and layout</li>
                            <li>• Product descriptions and images</li>
                            <li>• WillPower Store branding</li>
                            <li>• Original written content</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="mb-3 font-bold font-heading text-foreground">
                            Third-Party Content
                          </h4>
                          <ul className="space-y-1 text-muted-foreground text-sm">
                            <li>• Product brand names and logos</li>
                            <li>• Manufacturer product information</li>
                            <li>• Licensed product images</li>
                            <li>• Third-party trademarks</li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <div className="rounded-lg bg-muted/30 p-4">
                    <p className="text-muted-foreground text-sm">
                      You may not reproduce, distribute, or create derivative
                      works from our content without written permission.
                    </p>
                  </div>
                </div>
              </motion.section>

              <Separator />

              {/* Section 10: Limitation of Liability */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <h2 className="mb-6 font-bold font-heading text-2xl text-foreground md:text-3xl">
                  10. Limitation of Liability
                </h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-optimal leading-relaxed">
                    To the maximum extent permitted by UK law:
                  </p>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="space-y-4">
                        <div>
                          <h4 className="mb-2 font-bold font-heading text-foreground">
                            Excluded Damages
                          </h4>
                          <p className="text-muted-foreground text-sm">
                            We shall not be liable for any indirect, incidental,
                            special, or consequential damages arising from your
                            use of our website or products.
                          </p>
                        </div>
                        <div>
                          <h4 className="mb-2 font-bold font-heading text-foreground">
                            Maximum Liability
                          </h4>
                          <p className="text-muted-foreground text-sm">
                            Our total liability to you shall not exceed the
                            amount you paid for the specific product or service
                            giving rise to the claim.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <div className="rounded-lg bg-muted/30 p-4">
                    <p className="text-muted-foreground text-sm">
                      <strong>Important:</strong> Nothing in these terms
                      excludes or limits our liability for death or personal
                      injury caused by negligence, fraud, or other matters where
                      exclusion is prohibited by law.
                    </p>
                  </div>
                </div>
              </motion.section>

              <Separator />

              {/* Section 11: Governing Law and Dispute Resolution */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <h2 className="mb-6 font-bold font-heading text-2xl text-foreground md:text-3xl">
                  11. Governing Law and Dispute Resolution
                </h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-optimal leading-relaxed">
                    These terms are governed by English law and UK jurisdiction:
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Card>
                      <CardContent className="pt-6">
                        <h4 className="mb-3 font-bold font-heading text-foreground">
                          Applicable Law
                        </h4>
                        <ul className="space-y-1 text-muted-foreground text-sm">
                          <li>• English and Welsh law applies</li>
                          <li>• UK consumer protection regulations</li>
                          <li>• EU-derived consumer rights</li>
                          <li>• Birmingham local trading standards</li>
                        </ul>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <h4 className="mb-3 font-bold font-heading text-foreground">
                          Dispute Resolution
                        </h4>
                        <ul className="space-y-1 text-muted-foreground text-sm">
                          <li>• English courts have exclusive jurisdiction</li>
                          <li>• Alternative dispute resolution available</li>
                          <li>• Consumer ombudsman services</li>
                          <li>• Trading standards mediation</li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </motion.section>

              <Separator />

              {/* Section 12: Changes to Terms */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <h2 className="mb-6 font-bold font-heading text-2xl text-foreground md:text-3xl">
                  12. Changes to Terms
                </h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-optimal leading-relaxed">
                    We may update these terms from time to time to reflect
                    changes in our business or legal requirements:
                  </p>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="space-y-4">
                        <div>
                          <h4 className="mb-2 font-bold font-heading text-foreground">
                            Notification Process
                          </h4>
                          <p className="text-muted-foreground text-sm">
                            Changes will be posted on this page with an updated
                            date. For significant changes, we will notify you
                            via email or prominent website notice.
                          </p>
                        </div>
                        <div>
                          <h4 className="mb-2 font-bold font-heading text-foreground">
                            Your Options
                          </h4>
                          <p className="text-muted-foreground text-sm">
                            Continued use of our website after changes
                            constitutes acceptance. If you disagree with
                            changes, please discontinue use of our services.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </motion.section>
            </div>

            {/* Contact Section */}
            <motion.div
              className="mt-16 text-center"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <Card>
                <CardContent className="pt-8 pb-8">
                  <h3 className="mb-4 font-bold font-heading text-foreground text-xl">
                    Questions About These Terms?
                  </h3>
                  <p className="mx-auto mb-6 max-w-2xl text-muted-foreground text-optimal">
                    If you have any questions about these terms and conditions
                    or need clarification on any aspect of our service, please
                    contact us via email or Instagram.
                  </p>
                  <div className="flex flex-col justify-center gap-4 sm:flex-row">
                    <Button asChild size="lg">
                      <a href="mailto:willpowerwellbeing@hotmail.com">
                        Email Us
                      </a>
                    </Button>
                    <Button asChild size="lg" variant="outline">
                      <a
                        href="https://instagram.com/willpowergymstoreuk"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        Instagram DM
                      </a>
                    </Button>
                  </div>
                  <div className="mx-auto mt-4 max-w-lg text-muted-foreground text-sm">
                    <p>
                      <strong>Response Times:</strong> We usually reply within
                      48 hours for both email and Instagram messages.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
}
